import { Capacitor } from '@capacitor/core'

const INSTALL_DISMISS_KEY = 'imobdesk_pwa_install_dismissed_at'
export const PWA_INSTALL_DISMISS_DAYS = 7
const INSTALL_DISMISS_MS = PWA_INSTALL_DISMISS_DAYS * 24 * 60 * 60 * 1000

type InstallPromptListener = (
  prompt: BeforeInstallPromptEvent | null,
) => void

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null
let installPromptCaptureBound = false
const installPromptListeners = new Set<InstallPromptListener>()

/**
 * Escuta `beforeinstallprompt` o mais cedo possível (antes do React montar).
 * O Chrome dispara o evento uma vez; se o listener ainda não existir, o Instalar
 * fica sem prompt e o botão parece morto.
 */
export function capturePwaInstallPromptEarly(): void {
  if (typeof window === 'undefined' || installPromptCaptureBound) return
  installPromptCaptureBound = true

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredInstallPrompt = event as BeforeInstallPromptEvent
    for (const listener of installPromptListeners) {
      listener(deferredInstallPrompt)
    }
  })
}

export function getDeferredPwaInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredInstallPrompt
}

export function subscribePwaInstallPrompt(
  listener: InstallPromptListener,
): () => void {
  installPromptListeners.add(listener)
  listener(deferredInstallPrompt)
  return () => {
    installPromptListeners.delete(listener)
  }
}

export function clearDeferredPwaInstallPrompt(): void {
  deferredInstallPrompt = null
  for (const listener of installPromptListeners) {
    listener(null)
  }
}

export function isPwaNativeOrInstalled(): boolean {
  if (typeof window === 'undefined') return true
  if (Capacitor.isNativePlatform()) return true
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone) {
    return true
  }
  return false
}

export function isPwaInstallDismissed(): boolean {
  try {
    const raw = localStorage.getItem(INSTALL_DISMISS_KEY)
    if (!raw) return false
    const dismissedAt = Number.parseInt(raw, 10)
    if (Number.isNaN(dismissedAt)) return false
    return Date.now() - dismissedAt < INSTALL_DISMISS_MS
  } catch {
    return false
  }
}

export function dismissPwaInstallBanner(): void {
  try {
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()))
  } catch {
    // ignore
  }
}

export function canShowPwaUi(): boolean {
  return import.meta.env.PROD && !isPwaNativeOrInstalled()
}

export function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(ua)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome/.test(ua)
  return isIos && isSafari
}

export function isMobileBrowser(): boolean {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    window.navigator.userAgent,
  )
}

export function isDesktopBrowser(): boolean {
  return !isMobileBrowser()
}
